import * as React from 'react';

import {useQuery} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';
import {isFirstLoad} from 'nutshell-core/utils/graphql-loading';

import type {BroadcastSortType} from 'nutshell-graphql-types';
import type {
    GetTemplates as GetTemplatesQuery,
    GetTemplatesVariables as GetTemplatesQueryVariables,
    TemplateStubFragment as TemplateStub,
} from 'nutshell-graphql-types';

import GET_TEMPLATES from '../editions/graphql/queries/get-templates.graphql';

type Props = {
    isArchived: boolean;
    sortOrder: BroadcastSortType;
    searchQuery?: string;
    children: (arg1: {
        templates: TemplateStub[];
        error: string | null | undefined;
        isLoading: boolean;
        isFirstLoad: boolean;
        fetchMoreRows: () => Promise<any>;
        hasNextPage: boolean;
    }) => React.ReactElement;
};

const LIMIT = 20;

export function TemplateListQuery(props: Props) {
    const variables = getTemplateListQueryVariables(
        props.isArchived,
        props.sortOrder,
        props.searchQuery
    );

    const {data, networkStatus, error, fetchMore, loading} = useQuery<
        GetTemplatesQuery,
        GetTemplatesQueryVariables
    >(GET_TEMPLATES, {
        variables: variables,
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    const templatesData = data ? data.templates : null;
    const templates =
        templatesData && templatesData.edges ? templatesData.edges.map((edge) => edge.node) : [];

    const fetchMoreRows = () => {
        return fetchMore({
            variables: {
                first: LIMIT,
                after: templatesData ? templatesData.pageInfo.endCursor : null,
            },
            // @ts-expect-error
            updateQuery: (
                prev: GetTemplatesQuery,
                newResults: {
                    fetchMoreResult: GetTemplatesQuery;
                }
            ) => {
                const oldTemplates = prev.templates;
                const fetchedTemplates = newResults.fetchMoreResult.templates;

                const newTemplates = {
                    ...oldTemplates,
                    pageInfo: fetchedTemplates.pageInfo,
                    edges: oldTemplates.edges.concat(fetchedTemplates.edges),
                } as const;

                return {templates: newTemplates};
            },
        });
    };

    const hasNextPage = templatesData ? templatesData.pageInfo.hasNextPage : false;

    return props.children({
        templates,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
        isLoading: loading,
        isFirstLoad: isFirstLoad(networkStatus),
        fetchMoreRows,
        hasNextPage,
    });
}

export function getTemplateListQueryVariables(
    isArchived: boolean,
    sort: BroadcastSortType,
    search?: string | null
): GetTemplatesQueryVariables {
    return {
        // @ts-expect-error
        limit: 20,
        archived: isArchived,
        sort,
        search,
    };
}
